import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSalesHistory, getWeekBounds } from '../hooks/useSalesHistory';
import { CollectionSettingsModal } from '../components/sales/CollectionSettingsModal';
import { NotificationEmailsModal } from '../components/sales/NotificationEmailsModal';
import { Button } from '../components/ui/Button';
import { ShoppingCart, Calendar, Mail, Play, Download, Search, Filter, ChevronLeft, ChevronRight, Package, Clock, CircleCheck as CheckCircle, Circle as XCircle, TriangleAlert as AlertTriangle, Loader as Loader2, RefreshCw, ChevronDown, ChevronUp, ChartBar as BarChart3, FolderOpen, Layers } from 'lucide-react';

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; darkBg: string }> = {
  pending: { label: 'Pending', color: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-50', darkBg: 'dark:bg-amber-900/20' },
  reserved: { label: 'Reserved', color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50', darkBg: 'dark:bg-emerald-900/20' },
  not_available: { label: 'Not Available', color: 'text-red-700 dark:text-red-300', bg: 'bg-red-50', darkBg: 'dark:bg-red-900/20' },
  collected: { label: 'Collected', color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100', darkBg: 'dark:bg-gray-700' },
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatWeekLabel(start: string, end: string): string {
  const s = new Date(start + 'T12:00:00Z');
  const e = new Date(end + 'T12:00:00Z');
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', timeZone: 'UTC' };
  return `${s.toLocaleDateString('en-US', opts)} - ${e.toLocaleDateString('en-US', { ...opts, year: 'numeric' })}`;
}

const COL_COUNT = 8;

export const SalesHistory: React.FC = () => {
  const { user } = useAuth();
  const {
    consolidatedHistory,
    weekStats,
    loading,
    totalCount,
    collectionSettings,
    notificationEmails,
    loadSalesHistory,
    loadWeekStats,
    getLatestWeekWithData,
    triggerManualReservation,
    markAsCollected,
    loadCollectionSettings,
    saveCollectionSettings,
    loadNotificationEmails,
    addNotificationEmail,
    removeNotificationEmail,
    toggleNotificationEmail,
    setNotificationEmailFormat,
    sendTestEmail,
    downloadPickList,
  } = useSalesHistory();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [currentWeek, setCurrentWeek] = useState(getWeekBounds());
  const initialWeekResolved = useRef(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [showCollectionSettings, setShowCollectionSettings] = useState(false);
  const [showNotificationEmails, setShowNotificationEmails] = useState(false);
  const [reserving, setReserving] = useState(false);
  const [reserveResult, setReserveResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [collecting, setCollecting] = useState(false);
  const [collectResult, setCollectResult] = useState<{ success: boolean; message: string } | null>(null);

  const refreshData = useCallback(() => {
    loadSalesHistory({
      weekStart: currentWeek.weekStart,
      weekEnd: currentWeek.weekEnd,
      status: statusFilter,
      search: searchQuery,
    });
    loadWeekStats(currentWeek.weekStart, currentWeek.weekEnd);
  }, [currentWeek, statusFilter, searchQuery, loadSalesHistory, loadWeekStats]);

  useEffect(() => {
    setSelectedKeys(new Set());
    setExpandedKeys(new Set());
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    loadCollectionSettings();
    loadNotificationEmails();
  }, [loadCollectionSettings, loadNotificationEmails]);

  // On first load, land on the most recent week that actually has sales rather
  // than a freshly-rolled-over (and therefore empty) current week. Runs once;
  // the week navigator and "Today" button still let the user move freely.
  useEffect(() => {
    if (initialWeekResolved.current) return;
    initialWeekResolved.current = true;

    void (async () => {
      const latest = await getLatestWeekWithData();
      const cur = getWeekBounds();
      if (latest && latest.weekStart !== cur.weekStart) {
        setCurrentWeek(latest);
        setPage(1);
      }
    })();
  }, [getLatestWeekWithData]);

  const goToPreviousWeek = () => {
    const d = new Date(currentWeek.weekStart + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() - 7);
    setCurrentWeek(getWeekBounds(d));
    setPage(1);
  };

  const goToNextWeek = () => {
    const d = new Date(currentWeek.weekStart + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + 7);
    setCurrentWeek(getWeekBounds(d));
    setPage(1);
  };

  const goToCurrentWeek = () => {
    setCurrentWeek(getWeekBounds());
    setPage(1);
  };

  const isCurrentWeek = currentWeek.weekStart === getWeekBounds().weekStart;

  const handleReserveNow = async () => {
    if (!confirm('Create a reservation from all pending sales this week? This will check warehouse availability and create an In-house Shipment reservation.')) return;

    setReserving(true);
    setReserveResult(null);
    const result = await triggerManualReservation(currentWeek.weekStart, currentWeek.weekEnd);
    setReserveResult(result);
    setReserving(false);
    if (result.success) {
      refreshData();
    }
    setTimeout(() => setReserveResult(null), 8000);
  };

  const paginatedItems = useMemo(() => {
    const from = (page - 1) * pageSize;
    return consolidatedHistory.slice(from, from + pageSize);
  }, [consolidatedHistory, page, pageSize]);

  const totalPages = Math.ceil(totalCount / pageSize);

  const categoryItemCounts = useMemo(() => {
    const counts: Record<string, { items: number; qty: number }> = {};
    for (const item of consolidatedHistory) {
      if (!counts[item.category]) counts[item.category] = { items: 0, qty: 0 };
      counts[item.category].items++;
      counts[item.category].qty += item.total_quantity;
    }
    return counts;
  }, [consolidatedHistory]);

  const selectableOnPage = useMemo(
    () => paginatedItems.filter(i => i.primary_status === 'reserved'),
    [paginatedItems]
  );

  const allSelectedOnPage = selectableOnPage.length > 0 && selectableOnPage.every(i => selectedKeys.has(i.key));

  const toggleExpand = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelect = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelectedOnPage) {
      setSelectedKeys(prev => {
        const next = new Set(prev);
        selectableOnPage.forEach(i => next.delete(i.key));
        return next;
      });
    } else {
      setSelectedKeys(prev => {
        const next = new Set(prev);
        selectableOnPage.forEach(i => next.add(i.key));
        return next;
      });
    }
  };

  const selectedItems = useMemo(
    () => consolidatedHistory.filter(i => selectedKeys.has(i.key)),
    [consolidatedHistory, selectedKeys]
  );
  const selectedTotalUnits = selectedItems.reduce((sum, i) => sum + i.total_quantity, 0);

  const handleMarkCollected = async () => {
    if (selectedItems.length === 0) return;
    const allIds = selectedItems.flatMap(i => i.sale_ids);
    setCollecting(true);
    setCollectResult(null);
    const ok = await markAsCollected(allIds);
    setCollecting(false);
    if (ok) {
      setCollectResult({ success: true, message: `Marked ${selectedItems.length} items (${allIds.length} records) as collected` });
      setSelectedKeys(new Set());
      refreshData();
    } else {
      setCollectResult({ success: false, message: 'Failed to mark items as collected' });
    }
    setTimeout(() => setCollectResult(null), 6000);
  };

  const statCards = [
    {
      label: 'Sold This Week',
      value: weekStats.total_sold,
      icon: ShoppingCart,
      accent: 'from-teal-500 to-teal-600',
      iconBg: 'bg-teal-50 dark:bg-teal-900/30',
      iconColor: 'text-teal-600 dark:text-teal-400',
    },
    {
      label: 'Pending Reservation',
      value: weekStats.pending,
      icon: Clock,
      accent: 'from-amber-500 to-amber-600',
      iconBg: 'bg-amber-50 dark:bg-amber-900/30',
      iconColor: 'text-amber-600 dark:text-amber-400',
    },
    {
      label: 'Reserved',
      value: weekStats.reserved,
      icon: CheckCircle,
      accent: 'from-emerald-500 to-emerald-600',
      iconBg: 'bg-emerald-50 dark:bg-emerald-900/30',
      iconColor: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      label: 'Not Available',
      value: weekStats.not_available,
      icon: XCircle,
      accent: 'from-red-500 to-red-600',
      iconBg: 'bg-red-50 dark:bg-red-900/30',
      iconColor: 'text-red-600 dark:text-red-400',
    },
  ];

  const renderTableRows = () => {
    const rows: React.ReactNode[] = [];
    let lastCategory = '';

    for (const item of paginatedItems) {
      if (item.category !== lastCategory) {
        lastCategory = item.category;
        const catInfo = categoryItemCounts[item.category];
        rows.push(
          <tr key={`cat-${item.category}`} className="bg-gray-100/80 dark:bg-gray-700/60">
            <td colSpan={COL_COUNT} className="px-5 py-2.5">
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                <span className="text-sm font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                  {item.category}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                  {catInfo?.items || 0} items &middot; {catInfo?.qty || 0} units
                </span>
              </div>
            </td>
          </tr>
        );
      }

      const statusCfg = STATUS_CONFIG[item.primary_status] || STATUS_CONFIG.pending;
      const isNotAvailable = item.primary_status === 'not_available';
      const isExpanded = expandedKeys.has(item.key);
      const isSelectable = item.primary_status === 'reserved';
      const isSelected = selectedKeys.has(item.key);
      const hasMultipleRecords = item.records.length > 1;

      rows.push(
        <tr
          key={item.key}
          className={`transition-colors ${
            isNotAvailable ? 'bg-red-50/40 dark:bg-red-900/5' : ''
          } ${isSelected ? 'bg-teal-50/60 dark:bg-teal-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
        >
          <td className="px-3 py-4 w-10">
            {isSelectable ? (
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggleSelect(item.key)}
                className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-teal-600 focus:ring-teal-500 cursor-pointer"
              />
            ) : (
              <div className="h-4 w-4" />
            )}
          </td>
          <td className="px-5 py-4">
            <div className="flex items-center gap-2">
              {hasMultipleRecords && (
                <button
                  onClick={() => toggleExpand(item.key)}
                  className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 dark:text-gray-500 transition-colors flex-shrink-0"
                >
                  {isExpanded
                    ? <ChevronUp className="h-3.5 w-3.5" />
                    : <ChevronDown className="h-3.5 w-3.5" />
                  }
                </button>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[200px]">
                  {item.product_name}
                </p>
                {item.reference_code && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Ref: {item.reference_code}
                  </p>
                )}
              </div>
            </div>
          </td>
          <td className="px-5 py-4 hidden md:table-cell">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              {[item.color_name, item.size_name].filter(Boolean).join(' / ') || '-'}
            </div>
          </td>
          <td className="px-5 py-4 hidden lg:table-cell">
            <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">
              {item.sku || '-'}
            </span>
          </td>
          <td className="px-5 py-4 text-right">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {item.unit_price.toFixed(2)}
            </span>
          </td>
          <td className="px-5 py-4 text-right">
            <div className="flex items-center justify-end gap-1.5">
              <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                {item.total_quantity}
              </span>
              {hasMultipleRecords && (
                <span className="text-[10px] text-gray-400 dark:text-gray-500 font-medium">
                  ({item.records.length}x)
                </span>
              )}
            </div>
          </td>
          <td className="px-5 py-4 hidden sm:table-cell">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {formatDate(item.latest_sold_at)}
            </span>
          </td>
          <td className="px-5 py-4">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusCfg.bg} ${statusCfg.darkBg} ${statusCfg.color}`}>
              {statusCfg.label}
            </span>
          </td>
        </tr>
      );

      if (isExpanded) {
        for (const record of item.records) {
          const recStatus = STATUS_CONFIG[record.reservation_status] || STATUS_CONFIG.pending;
          rows.push(
            <tr
              key={`detail-${record.id}`}
              className="bg-gray-50/70 dark:bg-gray-800/50 border-l-4 border-teal-300 dark:border-teal-700"
            >
              <td className="px-3 py-2.5 w-10" />
              <td className="px-5 py-2.5 pl-12">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {record.shopify_order_number || 'No order #'}
                </span>
              </td>
              <td className="px-5 py-2.5 hidden md:table-cell">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {[record.color_name, record.size_name].filter(Boolean).join(' / ') || '-'}
                </span>
              </td>
              <td className="px-5 py-2.5 hidden lg:table-cell">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                  {record.sku || '-'}
                </span>
              </td>
              <td className="px-5 py-2.5 text-right">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {record.unit_price.toFixed(2)}
                </span>
              </td>
              <td className="px-5 py-2.5 text-right">
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  {record.quantity_sold}
                </span>
              </td>
              <td className="px-5 py-2.5 hidden sm:table-cell">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {formatDate(record.sold_at)}
                </span>
              </td>
              <td className="px-5 py-2.5">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${recStatus.bg} ${recStatus.darkBg} ${recStatus.color}`}>
                  {recStatus.label}
                </span>
              </td>
            </tr>
          );
        }
      }
    }

    return rows;
  };

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-lg shadow-teal-500/20">
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            Sales History
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-[52px]">
            Track Shopify sales from your Shop location
          </p>
        </div>

        {isAdmin && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setShowCollectionSettings(true)}
              variant="secondary"
              size="sm"
            >
              <Calendar className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Collection</span> Schedule
            </Button>
            <Button
              onClick={() => setShowNotificationEmails(true)}
              variant="secondary"
              size="sm"
            >
              <Mail className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Email</span> Recipients
            </Button>
            <Button
              onClick={handleReserveNow}
              variant="primary"
              size="sm"
              loading={reserving}
              disabled={weekStats.pending === 0}
            >
              <Play className="h-4 w-4 mr-1.5" />
              Reserve Now
            </Button>
          </div>
        )}
      </div>

      {/* Result Banners */}
      {reserveResult && (
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 text-sm font-medium animate-in fade-in ${
          reserveResult.success
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
        }`}>
          {reserveResult.success ? (
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
          ) : (
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          )}
          {reserveResult.message}
        </div>
      )}

      {collectResult && (
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 text-sm font-medium animate-in fade-in ${
          collectResult.success
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800'
        }`}>
          {collectResult.success ? (
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
          ) : (
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          )}
          {collectResult.message}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {card.label}
                  </p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                    {loading ? (
                      <span className="inline-block w-12 h-8 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                    ) : (
                      card.value
                    )}
                  </p>
                </div>
                <div className={`w-11 h-11 rounded-xl ${card.iconBg} flex items-center justify-center`}>
                  <Icon className={`h-5 w-5 ${card.iconColor}`} />
                </div>
              </div>
              <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${card.accent}`} />
            </div>
          );
        })}
      </div>

      {/* Week Navigator + Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
        <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousWeek}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="text-center min-w-[200px]">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {formatWeekLabel(currentWeek.weekStart, currentWeek.weekEnd)}
              </p>
              {isCurrentWeek && (
                <p className="text-xs text-teal-600 dark:text-teal-400 font-medium">Current Week</p>
              )}
            </div>
            <button
              onClick={goToNextWeek}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            {!isCurrentWeek && (
              <button
                onClick={goToCurrentWeek}
                className="ml-1 px-3 py-1.5 text-xs font-medium text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors"
              >
                Today
              </button>
            )}
          </div>

          <div className="flex-1" />

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
              placeholder="Search items, SKU, order..."
              className="pl-9 pr-4 py-2 w-full sm:w-64 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div className="relative">
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
            >
              <Filter className="h-4 w-4" />
              {statusFilter === 'all' ? 'All Status' : STATUS_CONFIG[statusFilter]?.label || statusFilter}
              <ChevronDown className="h-3 w-3" />
            </button>
            {showStatusDropdown && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowStatusDropdown(false)} />
                <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-20 py-1 overflow-hidden">
                  {[{ value: 'all', label: 'All Status' }, ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => { setStatusFilter(opt.value); setShowStatusDropdown(false); setPage(1); }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                        statusFilter === opt.value
                          ? 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 font-medium'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadPickList(currentWeek.weekStart, currentWeek.weekEnd)}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              title="Download CSV"
            >
              <Download className="h-4 w-4" />
            </button>
            <button
              onClick={refreshData}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden relative">
        {loading && consolidatedHistory.length === 0 ? (
          <div className="p-12 text-center">
            <Loader2 className="h-8 w-8 text-teal-500 animate-spin mx-auto mb-4" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading sales data...</p>
          </div>
        ) : consolidatedHistory.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Package className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">
              No sales detected
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'No Shopify sales from the Shop location this week'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-3 py-3.5 w-10">
                      <input
                        type="checkbox"
                        checked={allSelectedOnPage}
                        onChange={toggleSelectAll}
                        disabled={selectableOnPage.length === 0}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-teal-600 focus:ring-teal-500 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                        title={selectableOnPage.length === 0 ? 'No reserved items to select' : 'Select all reserved items'}
                      />
                    </th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Item</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Color / Size</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden lg:table-cell">SKU</th>
                    <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Price</th>
                    <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Qty</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">Sold</th>
                    <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {renderTableRows()}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                  <Layers className="h-3.5 w-3.5" />
                  {totalCount} consolidated items
                </div>
                <select
                  value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                  className="text-sm bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg px-2 py-1 text-gray-700 dark:text-gray-300"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400 px-3">
                    {page} / {totalPages || 1}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Floating Selection Action Bar */}
        {selectedKeys.size > 0 && (
          <div className="sticky bottom-0 left-0 right-0 bg-teal-600 dark:bg-teal-700 px-5 py-3 flex items-center justify-between shadow-lg border-t border-teal-500 dark:border-teal-600">
            <div className="flex items-center gap-3 text-white">
              <CheckCircle className="h-5 w-5" />
              <span className="text-sm font-medium">
                {selectedKeys.size} items selected ({selectedTotalUnits} units)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedKeys(new Set())}
                className="px-3 py-1.5 text-sm font-medium text-teal-100 hover:text-white hover:bg-teal-500 dark:hover:bg-teal-600 rounded-lg transition-colors"
              >
                Clear
              </button>
              <Button
                onClick={handleMarkCollected}
                loading={collecting}
                variant="primary"
                size="sm"
                className="!bg-white !text-teal-700 hover:!bg-teal-50 !shadow-sm"
              >
                <CheckCircle className="h-4 w-4 mr-1.5" />
                Mark as Collected
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <CollectionSettingsModal
        isOpen={showCollectionSettings}
        onClose={() => setShowCollectionSettings(false)}
        settings={collectionSettings}
        onSave={saveCollectionSettings}
      />

      <NotificationEmailsModal
        isOpen={showNotificationEmails}
        onClose={() => setShowNotificationEmails(false)}
        emails={notificationEmails}
        onAdd={addNotificationEmail}
        onRemove={removeNotificationEmail}
        onToggle={toggleNotificationEmail}
        onSetFormat={setNotificationEmailFormat}
        onSendTest={sendTestEmail}
      />
    </div>
  );
};
